package or.garden.manager.features.catalog

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import or.garden.manager.components.CardSkeleton
import or.garden.manager.domain.model.CropData
import or.garden.manager.features.catalog.components.CropDetailsView
import or.garden.manager.features.catalog.components.WebCropCard
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.I
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

@Composable
fun CatalogScreen(
    crops: List<CropData>,
    favoriteCrops: List<CropData>,
    isLoading: Boolean,
    isTogglingFavorite: Boolean,
    searchText: String,
    onSearchTextChange: (String) -> Unit,
    onToggleFavorite: (CropData) -> Unit
) {
    var selectedCropDetails by remember { mutableStateOf<CropData?>(null) }
    var selectedTab by remember { mutableStateOf(0) } // 0: Sve, 1: Omiljeni

    if (selectedCropDetails != null) {
        CropDetailsView(
            crop = crops.find { it.id == selectedCropDetails!!.id } ?: selectedCropDetails!!,
            onBack = { selectedCropDetails = null },
            onToggleFavorite = { onToggleFavorite(selectedCropDetails!!) },
            isTogglingFavorite = isTogglingFavorite
        )
    } else {
        Div({ classes("max-w-4xl", "mb-8") }) {
            Div({ classes("relative") }) {
                I({
                    classes(
                        "fas",
                        "fa-search",
                        "absolute",
                        "left-4",
                        "top-1/2",
                        "-translate-y-1/2",
                        "text-gray-400"
                    )
                })
                Input(InputType.Text) {
                    classes(
                        "w-full",
                        "pl-12",
                        "pr-4",
                        "py-4",
                        "bg-white",
                        "border",
                        "border-gray-200",
                        "rounded-2xl",
                        "focus:ring-2",
                        "focus:ring-emerald-500",
                        "outline-none",
                        "transition-all",
                        "shadow-sm"
                    )
                    placeholder("Pretraži kulture (npr. paradajz, paprika...)")
                    value(searchText)
                    onInput { onSearchTextChange(it.value) }
                }
            }
        }

        Div({
            classes("flex", "gap-8", "border-b", "border-gray-200", "mb-8")
        }) {
            val tabClass =
                arrayOf("pb-4", "font-bold", "transition-all", "cursor-pointer", "relative")
            val activeTabClass = arrayOf(
                "text-emerald-600",
                "after:absolute",
                "after:bottom-0",
                "after:left-0",
                "after:right-0",
                "after:h-1",
                "after:bg-emerald-600",
                "after:rounded-t-full"
            )
            val inactiveTabClass = arrayOf("text-gray-400", "hover:text-gray-600")

            Div({
                classes(*tabClass)
                if (selectedTab == 0) classes(*activeTabClass) else classes(*inactiveTabClass)
                onClick { selectedTab = 0 }
            }) { Text("Sve kulture") }

            Div({
                classes(*tabClass)
                if (selectedTab == 1) classes(*activeTabClass) else classes(*inactiveTabClass)
                onClick { selectedTab = 1 }
            }) {
                Div({ classes("flex", "items-center", "gap-2") }) {
                    Text("Omiljeni")
                    if (favoriteCrops.isNotEmpty()) {
                        Span({
                            classes(
                                "bg-emerald-100",
                                "text-emerald-700",
                                "text-xs",
                                "px-2",
                                "py-0.5",
                                "rounded-full"
                            )
                        }) {
                            Text(favoriteCrops.size.toString())
                        }
                    }
                }
            }
        }

        if (isLoading) {
            Div({
                classes("grid", "grid-cols-1", "md:grid-cols-2", "lg:grid-cols-3", "gap-4", "md:gap-6")
            }) {
                repeat(6) { CardSkeleton() }
            }
        } else {
            val displayedCrops = if (selectedTab == 0) crops else favoriteCrops

            if (displayedCrops.isEmpty()) {
                Div({ classes("text-center", "py-20", "text-gray-400") }) {
                    I({
                        classes(
                            "fas",
                            if (selectedTab == 1) "fa-heart-broken" else "fa-search",
                            "text-4xl",
                            "mb-4"
                        )
                    })
                    H2 { Text(if (selectedTab == 1) "Nemate omiljenih kultura." else "Nema kultura koje odgovaraju pretrazi.") }
                }
            } else {
                Div({
                    classes("grid", "grid-cols-1", "md:grid-cols-2", "lg:grid-cols-3", "gap-4", "md:gap-6")
                }) {
                    displayedCrops.filter { it.id != -1 }.sortedBy { it.name }.forEach { crop ->
                        WebCropCard(
                            crop = crop,
                            onToggleFavorite = { onToggleFavorite(crop) },
                            onClick = { selectedCropDetails = crop }
                        )
                    }
                }
            }
        }
    }
}
